/* eslint-disable no-console */
/* global google */
import React, { Component, createRef } from 'react';
import { connect } from 'react-redux';
import { useJsApiLoader } from '@react-google-maps/api';
import PropTypes from 'prop-types';
import { googleMapsLoaderOptions } from '../../../../utils/googleMaps';
import placeIconRestaurant from './images/local_dining.svg';
import placeIconBar from './images/local_bar.svg';
import placeIconShopping from './images/store_mall_directory.svg';
import placeIconResidential from './images/home.svg';
import placeIconHealth from './images/fitness_center.svg';
import placeIconTransit from './images/directions_transit.svg';
import propertyMarkerFlag from './images/property-marker.svg';
import './Map.css';

// The map is built imperatively with the Maps API (it needs the Places service and plain
// google.maps markers), so this component only renders the container element
class MapWithASearchBox extends Component {
    mapElement = createRef();

    componentDidMount() {
        this.loadMap(this.props.selectedPlaceType);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.screen.width !== this.props.screen.width) return;
        if (prevProps.selectedPlaceType !== this.props.selectedPlaceType) {
            this.loadMap(this.props.selectedPlaceType);
        }
    }

    getCenter() {
        return {
            lat: this.props.property.geo[0],
            lng: this.props.property.geo[1],
        };
    }

    loadMap(selectedPlaceType) {
        const center = this.getCenter();

        const mapStyles = [
            {
                featureType: 'poi',
                elementType: 'labels',
                stylers: [ { visibility: 'off' } ],
            },
            {
                featureType: 'transit',
                elementType: 'labels.icon',
                stylers: [ { visibility: 'off' } ],
            },
        ];

        const map = new google.maps.Map(this.mapElement.current, {
            center,
            zoom: 16,
            disableDefaultUI: true,
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            styles: mapStyles,
        });

        const service = new google.maps.places.PlacesService(map),
            infowindow = new google.maps.InfoWindow(),
            placeTypesRestaurant = [ 'bakery', 'cafe', 'restaurant' ],
            placeTypesBar = [ 'bar', 'night_club' ],
            placeTypesTransit = [
                'airport',
                'bus_station',
                'car_rental',
                'parking',
                'subway_station',
                'train_station',
                'transit_station',
            ],
            placeTypesShopping = [
                'book_store',
                'clothing_store',
                'department_store',
                'home_goods_store',
                'jewelry_store',
                'shoe_store',
                'shopping_mall',
            ],
            placeTypesResidential = [
                'church',
                'hospital',
                'fire_station',
                'park',
                'police',
                'post_office',
                'school',
                'supermarket',
                'zoo',
            ],
            placeTypesHealth = [
                'beauty_salon',
                'dentist',
                'doctor',
                'gym',
                'hair_care',
                'spa',
            ];

        let placeType = {},
            iconColor,
            iconPath;

        // match all the place types with the selected filter
        const getMatchingPlaceTypes = selectedPlaceType => {
            switch (selectedPlaceType) {
                case 'restaurant':
                    iconColor = '#4295d1';
                    placeType = placeTypesRestaurant;
                    iconPath = placeIconRestaurant;
                    break;
                case 'bar':
                    iconColor = '#858b9d';
                    placeType = placeTypesBar;
                    iconPath = placeIconBar;
                    break;
                case 'store':
                    iconColor = '#37c2d8';
                    placeType = placeTypesShopping;
                    iconPath = placeIconShopping;
                    break;
                case 'park':
                    iconColor = '#febe10';
                    placeType = placeTypesResidential;
                    iconPath = placeIconResidential;
                    break;
                case 'gym':
                    iconColor = '#9c2872';
                    placeType = placeTypesHealth;
                    iconPath = placeIconHealth;
                    break;
                case 'transit_station':
                    iconColor = '#22598e';
                    placeType = placeTypesTransit;
                    iconPath = placeIconTransit;
                    break;
                default:
                    iconColor = '#4295d1';
                    placeType = placeTypesRestaurant;
                    iconPath = placeIconRestaurant;
            }
            return placeType;
        };

        // maps api callback
        const serviceCallBack = (results, status) => {
            if (results === null) {
                return;
            } else if (
                status === google.maps.places.PlacesServiceStatus.OK
            ) {
                for (var i = 0; i < results.length; i++) {
                    createMarker(results[i]);
                }
            }
        };

        let addPlace = p => {
            if (p === undefined) {
                return;
            }
            placeType = p;
            service.nearbySearch(
                {
                    location: center,
                    radius: 2000,
                    type: [ placeType ],
                },
                serviceCallBack
            );
        };

        // add the places for each place type
        for (var i = 0; i < getMatchingPlaceTypes(selectedPlaceType).length; i++) {
            addPlace(getMatchingPlaceTypes(selectedPlaceType)[i]);
        }

        // add the property marker
        new google.maps.Marker({
            map: map,
            icon: {
                url: propertyMarkerFlag,
                size: new google.maps.Size(97, 125), // actual size
                anchor: new google.maps.Point(16.5, 104), // offset the flag pole
            },
            position: {
                lat: this.props.property.geo[0],
                lng: this.props.property.geo[1],
            },
            title: 'property-marker',
            zIndex: 3,
        });

        // add the place markers
        const createMarker = place => {
            let marker = new google.maps.Marker({
                map: map,
                icon: {
                    path:
                        'M0-32A11.8,11.8,0,0,0-12-20.4C-12-10.07,0,0,0,0S12-10.27,12-20.4A11.8,11.8,0,0,0,0-32Z',
                    fillColor: iconColor,
                    fillOpacity: 1,
                    strokeColor: 'transparent',
                    strokeWeight: 1,
                },
                position: place.geometry.location,
                title: 'marker',
                zIndex: 1,
            });

            // add the InfoWindow for each marker
            bindClick(marker, place);

            let placeIcon = new google.maps.Marker({
                map: map,
                icon: {
                    url: iconPath,
                    anchor: new google.maps.Point(10, 28), // offset the flag pole
                },
                position: place.geometry.location,
                zIndex: 2,
                title: 'place-icon',
            });

            // add the InfoWindow for each place icon
            bindClick(placeIcon, place);
        };

        const bindClick = (marker, place) => {
            // add the InfoWindow for each marker
            google.maps.event.addListener(marker, 'click', function() {
                infowindow.setContent(place.name);
                infowindow.open(map, this);

                /*
                    Requesting the specific fields are used,
                    in order to get the basic data SKU
                    and avoid extra costs for Contact/atmosphere data
                 */
                var request = {
                    reference: place.reference,
                    fields: [
                        'formatted_address',
                        'name',
                        'url',
                    ],
                };

                service.getDetails(request, function(details, status) {
                    var str_adress = details.formatted_address;
                    var str_adress_array = str_adress.split(',', 3);

                    str_adress = str_adress_array.join();
                    infowindow.setContent(
                        '<div class="info-window"><strong>' +
                            details.name +
                            '</strong><br>' +
                            str_adress +
                            '<br>' +
                            '<a target="_blank" href="' +
                            details.url +
                            '">View on Google</a></div>'
                    );
                    infowindow.open(map, marker);
                });
            });
        };
    }

    render() {
        return (
            <div id="map-container-element">
                <div id="map" className="property-detail-map" ref={this.mapElement} />
            </div>
        );
    }
}

MapWithASearchBox.propTypes = {
    screen: PropTypes.object.isRequired,
    property: PropTypes.object.isRequired,
    selectedPlaceType: PropTypes.string.isRequired,
};

const Map = ({ selectedPlaceType, screen, property }) => {
    const { isLoaded } = useJsApiLoader(googleMapsLoaderOptions);

    if (!isLoaded) {
        return <div id="map-loading-element" />;
    }

    return (
        <MapWithASearchBox
            selectedPlaceType={selectedPlaceType}
            screen={screen}
            property={property}
        />
    );
};

Map.propTypes = {
    screen: PropTypes.object.isRequired,
    property: PropTypes.object.isRequired,
    selectedPlaceType: PropTypes.string.isRequired,
};

export default connect(
    ({ screen }) => ({ screen }),
    null
)(Map);
